import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Header from "@/components/Header";
import { toast } from "sonner";
import { Shield, Trash2, ArrowRight, CalendarDays } from "lucide-react";

const GRAD_YEARS = [2026, 2027, 2028, 2029, 2030, 2031, 2032, 2033, 2034];

export default function ClubBeta() {
  const { user } = useAuth();
  const isAD = user?.role === "athletic_director";

  return (
    <div className="min-h-screen bg-appbg text-white">
      <Header />
      <div className="px-4 md:px-8 lg:px-12 py-8 max-w-4xl">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-volt text-xs tracking-widest"><Shield size={14} /> CLUB COACH FINDER</div>
          <span className="bg-volt text-white px-2 py-0.5 text-[10px] tracking-widest heading" data-testid="club-beta-badge">BETA · INVITE ONLY</span>
        </div>
        <h1 className="heading text-4xl md:text-5xl mt-2">I&apos;M A CLUB<span className="text-blaze"> LOOKING FOR COACHES.</span></h1>
        <p className="text-neutral-400 mt-3 text-sm max-w-2xl">
          Describe your club and the commitment you need — season, practices, tournaments, and the grad-year teams to coach.
          We match you against {`4,000+`} lacrosse coaches in the TKG. <b className="text-white">Posting is free · unlimited postings · no card.</b>
        </p>

        {!isAD ? (
          <div className="mt-8 border border-volt/40 bg-appcard p-6" data-testid="club-signup-cta">
            <div className="text-xs tracking-widest text-volt">GET STARTED</div>
            <p className="text-sm text-neutral-300 mt-2">Create a free account, then come back to this page to set up your club and post your first coaching need.</p>
            <Link to="/auth?role=athletic_director" className="mt-4 inline-flex items-center gap-2 bg-blaze text-white heading text-lg px-8 py-4 hover:glow-blaze transition-all" data-testid="club-signup-btn">
              CREATE CLUB ACCOUNT <ArrowRight size={16} />
            </Link>
          </div>
        ) : (
          <ClubConsole />
        )}
      </div>
    </div>
  );
}

function ClubConsole() {
  const [profile, setProfile] = useState({ name: "", location: "", description: "", gender: "Boys" });
  const [hasProfile, setHasProfile] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/club/profile").then(({ data }) => {
      if (data?.name) { setProfile((p) => ({ ...p, ...data })); setHasProfile(true); }
    }).catch(() => {});
    api.get("/club/jobs").then(({ data }) => setJobs(data || [])).catch(() => {});
  };
  useEffect(load, []);

  const saveProfile = async () => {
    setBusy(true);
    try {
      await api.post("/club/profile", {
        name: profile.name, location: profile.location,
        description: profile.description, gender: profile.gender,
      });
      setHasProfile(true);
      toast.success("Club profile saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const removeJob = async (id) => {
    if (!window.confirm("Remove this posting?")) return;
    try { await api.delete(`/club/jobs/${id}`); setJobs((js) => js.filter((j) => j.id !== id)); toast.success("Posting removed"); }
    catch { toast.error("Remove failed"); }
  };

  const inp = "w-full bg-appbg border border-neutral-800 px-3 py-3 text-sm text-white outline-none focus:border-blaze";
  return (
    <div className="mt-8 space-y-6" data-testid="club-console">
      {/* Club profile */}
      <div className="border border-neutral-800 bg-appcard p-5">
        <div className="text-xs tracking-widest text-volt">STEP 1 · YOUR CLUB</div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input data-testid="club-name" className={inp} placeholder="Club name *" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} />
          <input data-testid="club-location" className={inp} placeholder="Location (city, state)" value={profile.location} onChange={(e) => setProfile({ ...profile, location: e.target.value })} />
          <select data-testid="club-gender" className={inp} value={profile.gender} onChange={(e) => setProfile({ ...profile, gender: e.target.value })}>
            <option>Boys</option><option>Girls</option><option>Both</option>
          </select>
        </div>
        <textarea data-testid="club-description" className={`${inp} mt-3 min-h-[90px]`} placeholder="Describe your club — history, level of play, culture…" value={profile.description} onChange={(e) => setProfile({ ...profile, description: e.target.value })} />
        <button data-testid="club-save-profile" onClick={saveProfile} disabled={busy || !profile.name.trim()} className="mt-3 min-h-[44px] px-6 bg-blaze text-white heading text-base hover:glow-blaze transition-all disabled:opacity-50">
          {busy ? "SAVING…" : hasProfile ? "UPDATE CLUB" : "SAVE CLUB"}
        </button>
      </div>

      {/* Subscription */}

      {/* New posting */}
      {hasProfile && <ClubJobForm onCreated={(j) => setJobs((js) => [j, ...js])} />}

      {/* Existing postings */}
      {jobs.length > 0 && (
        <div className="border border-neutral-800 bg-appcard p-5" data-testid="club-jobs-list">
          <div className="text-xs tracking-widest text-volt">YOUR POSTINGS ({jobs.length})</div>
          <div className="mt-3 space-y-2">
            {jobs.map((j) => (
              <div key={j.id} className="border border-neutral-800 bg-appbg p-3 flex items-start justify-between gap-3" data-testid={`club-job-${j.id}`}>
                <div className="min-w-0">
                  <div className="heading text-lg">{j.title}</div>
                  <div className="text-xs text-neutral-500 mt-0.5">
                    {j.club_meta?.practices_per_week} practices/wk{j.club_meta?.tournament_schedule ? ` · ${j.club_meta.tournament_schedule}` : ""}{j.club_meta?.pay_per_season ? ` · ${j.club_meta.pay_per_season}/season` : ""}{j.club_meta?.lodging_provided ? " · lodging provided" : ""}
                  </div>
                </div>
                <button data-testid={`club-job-delete-${j.id}`} onClick={() => removeJob(j.id)} className="text-neutral-500 hover:text-red-500 transition-colors shrink-0"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ClubJobForm({ onCreated }) {
  const [f, setF] = useState({ season: "Summer", coach_role: "Head Coach", practices_per_week: 2, tournament_schedule: "", pay_per_season: "", travel_perdiem: "", lodging_provided: false, housing_provided: false, notes: "" });
  const [gradYears, setGradYears] = useState([]);
  const [busy, setBusy] = useState(false);

  const toggleYear = (y) => setGradYears((g) => g.includes(y) ? g.filter((x) => x !== y) : [...g, y]);

  const post = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/club/jobs", { ...f, practices_per_week: Number(f.practices_per_week) || 1, grad_years: gradYears });
      onCreated(data);
      setGradYears([]);
      setF({ ...f, tournament_schedule: "", pay_per_season: "", travel_perdiem: "", lodging_provided: false, housing_provided: false, notes: "" });
      toast.success("Posting live — coaches can now see it in their feed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Posting failed");
    } finally { setBusy(false); }
  };

  const inp = "bg-appbg border border-neutral-800 px-3 py-3 text-sm text-white outline-none focus:border-blaze";
  return (
    <div className="border border-neutral-800 bg-appcard p-5" data-testid="club-job-form">
      <div className="flex items-center gap-2 text-xs tracking-widest text-volt"><CalendarDays size={13} /> POST A COACHING NEED</div>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <div className="text-[10px] tracking-widest text-neutral-500 mb-1">SEASON</div>
          <select data-testid="club-job-season" className={`${inp} w-full`} value={f.season} onChange={(e) => setF({ ...f, season: e.target.value })}>
            <option>Summer</option><option>Fall</option>
          </select>
        </div>
        <div>
          <div className="text-[10px] tracking-widest text-neutral-500 mb-1">ROLE</div>
          <select data-testid="club-job-role" className={`${inp} w-full`} value={f.coach_role} onChange={(e) => setF({ ...f, coach_role: e.target.value })}>
            <option>Head Coach</option><option>Assistant Coach</option>
          </select>
        </div>
        <div>
          <div className="text-[10px] tracking-widest text-neutral-500 mb-1">PRACTICES / WEEK</div>
          <input data-testid="club-job-practices" type="number" min="1" max="7" className={`${inp} w-full`} value={f.practices_per_week} onChange={(e) => setF({ ...f, practices_per_week: e.target.value })} />
        </div>
      </div>
      <div className="mt-3">
        <div className="text-[10px] tracking-widest text-neutral-500 mb-1">GRAD-YEAR TEAMS (SELECT ALL THAT APPLY)</div>
        <div className="flex flex-wrap gap-2">
          {GRAD_YEARS.map((y) => (
            <button
              key={y}
              type="button"
              data-testid={`club-grad-${y}`}
              onClick={() => toggleYear(y)}
              className={`px-3 py-2 text-xs heading border transition-colors ${gradYears.includes(y) ? "bg-blaze border-blaze text-white" : "border-neutral-700 text-neutral-400 hover:border-blaze hover:text-white"}`}
            >
              {y}s
            </button>
          ))}
        </div>
      </div>
      <input data-testid="club-job-tournaments" className={`${inp} w-full mt-3`} placeholder="Tournament schedule (e.g., 4 tournaments: June–July, NXT Philly + Lake Placid…)" value={f.tournament_schedule} onChange={(e) => setF({ ...f, tournament_schedule: e.target.value })} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
        <input data-testid="club-job-pay" className={inp} placeholder="Pay per season (e.g., $2,500 for Summer)" value={f.pay_per_season} onChange={(e) => setF({ ...f, pay_per_season: e.target.value })} />
        <input data-testid="club-job-perdiem" className={inp} placeholder="Travel per-diem (e.g., $50/day on tournament weekends)" value={f.travel_perdiem} onChange={(e) => setF({ ...f, travel_perdiem: e.target.value })} />
      </div>
      <label className="mt-3 flex items-center gap-2 text-sm text-neutral-300 cursor-pointer select-none">
        <input data-testid="club-job-lodging" type="checkbox" checked={f.lodging_provided} onChange={(e) => setF({ ...f, lodging_provided: e.target.checked })} className="accent-[rgb(var(--app-primary-rgb))] h-4 w-4" />
        Lodging provided by the club for travel tournaments
      </label>
      <label className="mt-2 flex items-center gap-2 text-sm text-neutral-300 cursor-pointer select-none">
        <input data-testid="club-job-housing" type="checkbox" checked={f.housing_provided} onChange={(e) => setF({ ...f, housing_provided: e.target.checked })} className="accent-[rgb(var(--app-primary-rgb))] h-4 w-4" />
        Season housing available for elite players relocating to coach
      </label>
      <input data-testid="club-job-notes" className={`${inp} w-full mt-3`} placeholder="Anything else coaches should know (optional)" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
      <button data-testid="club-job-post-btn" onClick={post} disabled={busy || gradYears.length === 0} className="mt-4 min-h-[48px] px-8 bg-blaze text-white heading text-base hover:glow-blaze transition-all disabled:opacity-50">
        {busy ? "POSTING…" : "POST COACHING NEED"}
      </button>
      {gradYears.length === 0 && <div className="text-[11px] text-neutral-600 mt-2">Select at least one grad-year team.</div>}
    </div>
  );
}
